/**
 * Тёмная и светлая темы — §13.2, критерий приёмки §13.4.
 *
 * Умолчание — системная тема (`prefers-color-scheme`): человек, у которого
 * телефон переключается на тёмную вечером, ждёт того же и здесь. Явный
 * выбор запоминается в `localStorage` и с этого момента главнее системного.
 *
 * Первое значение выставляется в <head> ещё до React (см. index.html) —
 * иначе тёмная тема моргает белым на каждой загрузке.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button } from './ui';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'toscana-theme';

function currentTheme(): Theme {
  const attribute = document.documentElement.dataset.theme;
  return attribute === 'dark' ? 'dark' : 'light';
}

export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(currentTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((previous) => {
      const next: Theme = previous === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Приватный режим браузера — тема просто не запомнится.
      }
      return next;
    });
  }, []);

  return [theme, toggle];
}

export function ThemeToggle() {
  const [theme, toggle] = useTheme();
  return (
    <Button
      small
      variant="quiet"
      onClick={toggle}
      aria-label={theme === 'dark' ? 'Включить светлую тему' : 'Включить тёмную тему'}
      title={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
    >
      {theme === 'dark' ? '☾ Тёмная' : '☀ Светлая'}
    </Button>
  );
}
